import { SORT_ORDER } from '@/constants'
import apiClient from '@/lib/apiClient'
import type { SortingState } from '@tanstack/react-table'
import { endpoints } from './endPoint'
import axios from 'axios'

export interface AttendeesParams {
  limit?: number
  page?: number
  search?: string
  sort?: SortingState
  status?: string[]
  purchaseDate?: string
  eventId?: string
  experience?: string
  persona?: string
}

export type reportType =
  | 'revenue'
  | 'daily_status'
  | 'attendee'
  | 'ticket_analysis'

export interface GenerateReportParams {
  startDate?: string
  endDate?: string
  reportType: reportType
}

export const fetchAttendees = async (params: AttendeesParams) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.limit) queryParams.append('limit', params?.limit?.toString())
    if (params.page) queryParams.append('page', params?.page?.toString())
    if (params.search) queryParams.append('search', params?.search)
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append(
        'order',
        params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
      )
    }
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
    }
    if (params.status) {
      params?.status?.forEach(s => {
        queryParams.append('statuses', s.split(' ').join('_').toLowerCase())
      })
    }

    if (params.purchaseDate) {
      queryParams.append('purchaseDate', params.purchaseDate)
    }

    if (params.eventId) {
      queryParams.append('eventId', params.eventId)
    }

    const response = await apiClient.get(
      `${endpoints.attendee.getAttendees}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching events:', error)
    throw error
  }
}

export async function getAttendeeById(
  transactionId: string,
  isAdmin: boolean = true
) {
  const resp = await apiClient.get(
    endpoints.attendee.getAttendeeById(transactionId),
    {
      params: {
        isAdmin,
      },
    }
  )
  return resp.data
}

export const getAttendeeExport = async (params: AttendeesParams) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.search) queryParams.append('search', params?.search)
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append(
        'order',
        params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
      )
    }
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
    }
    if (params.status) {
      params?.status?.forEach(s => {
        queryParams.append('statuses', s.split(' ').join('_').toLowerCase())
      })
    }

    if (params.purchaseDate) {
      queryParams.append('purchaseDate', params.purchaseDate)
    }

    if (params.eventId) {
      queryParams.append('eventId', params.eventId)
    }

    const response = await apiClient.get(endpoints.attendee.getExportAttendee, {
      params: queryParams,
      responseType: 'blob',
    })

    return response.data
  } catch (error) {
    console.error('Error exporting attendees:', error)
    throw error
  }
}

export const sendTicket = async (transactionId: string) => {
  try {
    const response = await apiClient.put(
      endpoints.attendee.sendTicket(transactionId)
    )
    return response.data
  } catch (error) {
    console.error('Error sending ticket:', error)
    throw error
  }
}

export const getEventAdminStatistics = async (params: {
  month?: string
  year?: string
}) => {
  const queryParams = new URLSearchParams()
  if (params?.month) queryParams.append('month', params?.month?.toString())
  if (params?.year) queryParams.append('year', params?.year?.toString())

  const response = await apiClient.get(
    `${endpoints.attendee.getEventAdminStatistics}?${queryParams.toString()}`
  )
  return response.data
}

export const getGenerateEventReport = async (
  params: GenerateReportParams
): Promise<Blob> => {
  const queryParams = new URLSearchParams()

  if (params.startDate) {
    queryParams.append('startDate', params.startDate)
  }

  if (params.endDate) {
    queryParams.append('endDate', params.endDate)
  }

  queryParams.append('reportType', params.reportType)

  const response = await apiClient.get(endpoints.attendee.generateEventReport, {
    params: queryParams,
    responseType: 'blob',
  })

  return response.data
}

export const getAttendeeBookingExport = async (params: AttendeesParams) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.search) queryParams.append('search', params?.search)
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append(
        'order',
        params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
      )
    }
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
    }
    if (params.status) {
      params?.status?.forEach(s => {
        queryParams.append('statuses', s.split(' ').join('_').toLowerCase())
      })
    }

    if (params.purchaseDate) {
      queryParams.append('purchaseDate', params.purchaseDate)
    }

    if (params.eventId) {
      queryParams.append('eventId', params.eventId)
    }

    if (params.experience) {
      queryParams.append('experience', params.experience)
    }

    if (params.persona) {
      queryParams.append('persona', params.persona)
    }

    const response = await apiClient.get(
      endpoints.attendee.getExportAttendeeBooking,
      {
        params: queryParams,
        responseType: 'blob',
      }
    )

    return response.data
  } catch (error) {
    console.error('Error exporting attendees:', error)
    throw error
  }
}

export const fetchAttendeeBookings = async (params: AttendeesParams) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.limit) queryParams.append('limit', params?.limit?.toString())
    if (params.page) queryParams.append('page', params?.page?.toString())
    if (params.search) queryParams.append('search', params?.search)
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append(
        'order',
        params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
      )
    }
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
    }
    if (params.status) {
      params?.status?.forEach(s => {
        queryParams.append('statuses', s.split(' ').join('_').toLowerCase())
      })
    }

    if (params.purchaseDate) {
      queryParams.append('purchaseDate', params.purchaseDate)
    }

    if (params.eventId) {
      queryParams.append('eventId', params.eventId)
    }

    if (params.experience) {
      queryParams.append('experience', params.experience)
    }

    if (params.persona) {
      queryParams.append('persona', params.persona)
    }

    const response = await apiClient.get(
      `${endpoints.attendee.getAttendeeBookings}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching events:', error)
    throw error
  }
}

export const downloadAttendeeConsentForm = async (consentFormKey: string) => {
  try {
    const params = new URLSearchParams({ key: consentFormKey })
    const downloadUrlResponse = await apiClient.get(
      `${endpoints.upload.getDownloadUrl}?${params.toString()}`
    )

    const downloadUrl = downloadUrlResponse.data.data.url

    const pdfResponse = await axios.get(downloadUrl, {
      responseType: 'blob',
    })

    return pdfResponse.data
  } catch (error) {
    console.error('Error downloading consent form:', error)
    throw error
  }
}
